import { useEffect, useMemo, useState } from "react";
import { useGlobalStore } from "../context/UserContext/userContext";
import {
  formatCreOrganisationCredits,
  getCreOrganisations,
  getCreOrganisationLabel,
  getDefaultCreOrganisation,
} from "./creOrganisation";

/**
 * Organisation list + continue (render inside CreOrganisationModal).
 */
export default function CreOrganisationPicker({
  title = "Select organisation",
  subtitle,
  continueLabel = "Continue",
  showCancel = false,
  onCancel,
  onContinue,
}) {
  const creUser = useGlobalStore((s) => s.creUser);
  const creSelectedOrganisationId = useGlobalStore(
    (s) => s.creSelectedOrganisationId,
  );
  const setCreSelectedOrganisation = useGlobalStore(
    (s) => s.setCreSelectedOrganisation,
  );

  const organisations = useMemo(
    () => getCreOrganisations(creUser),
    [creUser],
  );
  const defaultOrg = useMemo(
    () => getDefaultCreOrganisation(creUser),
    [creUser],
  );

  const [selectedId, setSelectedId] = useState(
    () =>
      creSelectedOrganisationId ||
      defaultOrg?.organisationId ||
      organisations[0]?.organisationId ||
      "",
  );

  useEffect(() => {
    if (creSelectedOrganisationId) {
      setSelectedId(creSelectedOrganisationId);
    } else if (defaultOrg?.organisationId) {
      setSelectedId(defaultOrg.organisationId);
    }
  }, [creSelectedOrganisationId, defaultOrg?.organisationId]);

  const handleContinue = () => {
    if (!selectedId) return;
    setCreSelectedOrganisation(selectedId);
    onContinue?.(selectedId);
  };

  return (
    <>
      <h2 id="cre-org-modal-title" className="cre-org-modal-title">
        {title}
      </h2>
      {subtitle ? <p className="cre-org-modal-sub">{subtitle}</p> : null}

      <ul className="cre-org-list" role="listbox" aria-label="Organisations">
        {organisations.map((org) => {
          const id = org.organisationId;
          const checked = selectedId === id;
          const credits = formatCreOrganisationCredits(org);

          return (
            <li key={id}>
              <button
                type="button"
                role="option"
                aria-selected={checked}
                className={`cre-org-option${checked ? " cre-org-option--selected" : ""}`}
                onClick={() => setSelectedId(id)}
              >
                <span className="cre-org-option__name">
                  {getCreOrganisationLabel(org)}
                  {org.isDefault ? (
                    <span className="cre-org-badge">Default</span>
                  ) : null}
                </span>
                {credits ? (
                  <span className="cre-org-option__meta">{credits}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="cre-org-modal-actions">
        {showCancel && onCancel ? (
          <button
            type="button"
            className="cre-org-btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="cre-btn-signin"
          disabled={!selectedId}
          onClick={handleContinue}
        >
          {continueLabel}
        </button>
      </div>
    </>
  );
}
